import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../src/components/ui/card';
import { Button } from '../../../../src/components/ui/button';
import { Input } from '../../../../src/components/ui/input';
import { Label } from '../../../../src/components/ui/label';
import { Slider } from '../../../../src/components/ui/slider';
import { Badge } from '../../../../src/components/ui/badge';
import {
  Calculator,
  TrendingUp,
  Clock,
  Users,
  CheckCircle,
  ArrowRight,
  Info,
  Award,
  Star,
} from 'lucide-react';
import { MONTANA_CONSTANTS } from '../../../../src/utils/entities/city/montana/montanaConstants';

interface CalculatorInputs {
  communitySize: number;

  // Meeting & Administration (Monthly hours)
  boardMeetingHours: number;
  meetingPrepHours: number;
  financialReviewHours: number;

  // Communication & Documentation
  residentCommunicationHours: number; // per week
  documentationHours: number; // per month

  // Enforcement & Compliance (Monthly)
  violationProcessingHours: number;
  architecturalReviewHours: number;
  legalComplianceHours: number;

  // Vendor & Project Management (Monthly)
  vendorManagementHours: number;
  maintenanceOversightHours: number;

  // Annual High-Impact Activities (Hours per year)
  budgetPreparationHours: number;
  annualMeetingPrepHours: number;
  insuranceRenewalHours: number;
  auditPreparationHours: number;
  emergencyResponseHours: number;

  selectedTier: 'silver' | 'gold' | 'both';
}

interface PackageResults {
  setup: number;
  monthlyRecurring: number;
  annualRecurring: number;

  // Comprehensive Time Savings (Annual Hours)
  timeSavings: {
    meetingEfficiency: { current: number; improved: number; savings: number };
    administrativeAutomation: { current: number; improved: number; savings: number };
    communicationStreamlining: { current: number; improved: number; savings: number };
    enforcementDigitization: { current: number; improved: number; savings: number };
    vendorManagement: { current: number; improved: number; savings: number };
    financialOversight: { current: number; improved: number; savings: number };
    emergencyResponse: { current: number; improved: number; savings: number };
    totalAnnualHoursSaved: number;
  };

  // Efficiency Improvements (%)
  efficiencyGains: {
    decisionSpeed: number;
    administrativeEfficiency: number;
    communicationClarity: number;
    processAutomation: number;
    boardSatisfaction: number;
  };

  // Time-based ROI Metrics
  timeROI: {
    totalImplementationHours: number; // Setup and learning curve
    annualTimeSaved: number;
    valueOfTimeSaved: number; // Dollar value of annual time saved (@ $75/hr board member time)
    netAnnualValue: number; // Value of time saved minus annual cost
    threeYearTimeSaved: number;
    threeYearValue: number; // Total 3-year value of time saved
    efficiencyMultiplier: number; // How many times more efficient
  };
}

interface CalculatorResults {
  silver: PackageResults;
  gold: PackageResults;
}

export const GovernanceEfficiencyCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    communitySize: MONTANA_CONSTANTS.DEFAULT_PARCELS,

    // Meeting & Administration (Monthly)
    boardMeetingHours: 3,
    meetingPrepHours: 4,
    financialReviewHours: 2,

    // Communication & Documentation (Weekly/Monthly)
    residentCommunicationHours: 5, // per week
    documentationHours: 8, // per month

    // Enforcement & Compliance (Monthly)
    violationProcessingHours: 6,
    architecturalReviewHours: 4,
    legalComplianceHours: 3,

    // Vendor & Project Management (Monthly)
    vendorManagementHours: 5,
    maintenanceOversightHours: 8,

    // Annual High-Impact Activities (Hours per year)
    budgetPreparationHours: 40,
    annualMeetingPrepHours: 25,
    insuranceRenewalHours: 12,
    auditPreparationHours: 15,
    emergencyResponseHours: 30,

    selectedTier: 'both',
  });

  const calculatePackageResults = (tier: 'silver' | 'gold'): PackageResults => {
    const setup =
      inputs.communitySize *
      MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL[
        tier.toUpperCase() as keyof typeof MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL
      ];
    const monthlyRecurring =
      inputs.communitySize *
      MONTANA_CONSTANTS.PRICING[tier.toUpperCase() as keyof typeof MONTANA_CONSTANTS.PRICING];
    const annualRecurring = monthlyRecurring * 12;

    // Community size scaling factor: larger communities need more governance work
    // Base scaling on ratio to reference community size (406 parcels)
    const baseSize = MONTANA_CONSTANTS.DEFAULT_PARCELS; // 406
    const sizeScalingFactor = Math.sqrt(inputs.communitySize / baseSize);
    // Using square root so it scales reasonably:
    // - 200 parcels: ~0.7x time (smaller community, less work)
    // - 406 parcels: 1.0x time (baseline)
    // - 800 parcels: ~1.4x time (larger community, more work)
    // - 1200 parcels: ~1.7x time

    // Calculate current annual time commitment (baseline) with community size scaling
    const currentAnnualHours = {
      meetings: (inputs.boardMeetingHours + inputs.meetingPrepHours) * 12 * sizeScalingFactor,
      communication: inputs.residentCommunicationHours * 52 * sizeScalingFactor,
      documentation: inputs.documentationHours * 12 * sizeScalingFactor,
      enforcement:
        (inputs.violationProcessingHours +
          inputs.architecturalReviewHours +
          inputs.legalComplianceHours) *
        12 *
        sizeScalingFactor,
      vendorManagement:
        (inputs.vendorManagementHours + inputs.maintenanceOversightHours) * 12 * sizeScalingFactor,
      financialOversight: inputs.financialReviewHours * 12 * sizeScalingFactor,
      annualActivities:
        (inputs.budgetPreparationHours +
          inputs.annualMeetingPrepHours +
          inputs.insuranceRenewalHours +
          inputs.auditPreparationHours) *
        sizeScalingFactor,
      emergencyResponse: inputs.emergencyResponseHours * sizeScalingFactor,
    };

    // Calculate efficiency improvements based on tier
    const efficiencyRates = {
      silver: {
        meetings: 0.25, // 25% more efficient meetings with digital tools
        communication: 0.4, // 40% faster with automated notifications
        documentation: 0.5, // 50% faster with templates and automation
        enforcement: 0.35, // 35% streamlined with digital workflow
        vendorManagement: 0.3, // 30% better with centralized management
        financialOversight: 0.45, // 45% faster with automated reporting
        annualActivities: 0.25, // 25% efficiency on big projects
        emergencyResponse: 0.2, // 20% faster coordination
      },
      gold: {
        meetings: 0.35, // 35% more efficient with advanced features
        communication: 0.55, // 55% faster with AI-assisted communication
        documentation: 0.65, // 65% faster with advanced automation
        enforcement: 0.5, // 50% streamlined with advanced workflow
        vendorManagement: 0.45, // 45% better with advanced project management
        financialOversight: 0.6, // 60% faster with advanced analytics
        annualActivities: 0.4, // 40% efficiency on big projects
        emergencyResponse: 0.35, // 35% faster with 24/7 digital access
      },
    };

    const rates = efficiencyRates[tier];

    // Calculate time savings for each category
    const timeSavings = {
      meetingEfficiency: {
        current: currentAnnualHours.meetings,
        improved: Math.round(currentAnnualHours.meetings * (1 - rates.meetings)),
        savings: Math.round(currentAnnualHours.meetings * rates.meetings),
      },
      administrativeAutomation: {
        current: currentAnnualHours.documentation + currentAnnualHours.annualActivities,
        improved: Math.round(
          currentAnnualHours.documentation * (1 - rates.documentation) +
            currentAnnualHours.annualActivities * (1 - rates.annualActivities),
        ),
        savings: Math.round(
          currentAnnualHours.documentation * rates.documentation +
            currentAnnualHours.annualActivities * rates.annualActivities,
        ),
      },
      communicationStreamlining: {
        current: currentAnnualHours.communication,
        improved: Math.round(currentAnnualHours.communication * (1 - rates.communication)),
        savings: Math.round(currentAnnualHours.communication * rates.communication),
      },
      enforcementDigitization: {
        current: currentAnnualHours.enforcement,
        improved: Math.round(currentAnnualHours.enforcement * (1 - rates.enforcement)),
        savings: Math.round(currentAnnualHours.enforcement * rates.enforcement),
      },
      vendorManagement: {
        current: currentAnnualHours.vendorManagement,
        improved: Math.round(currentAnnualHours.vendorManagement * (1 - rates.vendorManagement)),
        savings: Math.round(currentAnnualHours.vendorManagement * rates.vendorManagement),
      },
      financialOversight: {
        current: currentAnnualHours.financialOversight,
        improved: Math.round(
          currentAnnualHours.financialOversight * (1 - rates.financialOversight),
        ),
        savings: Math.round(currentAnnualHours.financialOversight * rates.financialOversight),
      },
      emergencyResponse: {
        current: currentAnnualHours.emergencyResponse,
        improved: Math.round(currentAnnualHours.emergencyResponse * (1 - rates.emergencyResponse)),
        savings: Math.round(currentAnnualHours.emergencyResponse * rates.emergencyResponse),
      },
      totalAnnualHoursSaved: 0, // Will be calculated below
    };

    // Calculate total time savings
    timeSavings.totalAnnualHoursSaved =
      timeSavings.meetingEfficiency.savings +
      timeSavings.administrativeAutomation.savings +
      timeSavings.communicationStreamlining.savings +
      timeSavings.enforcementDigitization.savings +
      timeSavings.vendorManagement.savings +
      timeSavings.financialOversight.savings +
      timeSavings.emergencyResponse.savings;

    // Calculate efficiency percentages
    const efficiencyGains = {
      decisionSpeed: tier === 'gold' ? 75 : 60,
      administrativeEfficiency: Math.round(
        (timeSavings.totalAnnualHoursSaved /
          (currentAnnualHours.meetings +
            currentAnnualHours.communication +
            currentAnnualHours.documentation +
            currentAnnualHours.enforcement +
            currentAnnualHours.vendorManagement +
            currentAnnualHours.financialOversight +
            currentAnnualHours.annualActivities +
            currentAnnualHours.emergencyResponse)) *
          100,
      ),
      communicationClarity: tier === 'gold' ? 85 : 75,
      processAutomation: tier === 'gold' ? 80 : 65,
      boardSatisfaction: tier === 'gold' ? 90 : 80,
    };

    // Time-based ROI calculations
    const implementationHours = tier === 'gold' ? 30 : 20; // Setup and training time
    const boardMemberHourlyValue = 75; // Conservative estimate of volunteer board member time value
    const annualValueOfTimeSaved = timeSavings.totalAnnualHoursSaved * boardMemberHourlyValue;
    const netAnnualValue = annualValueOfTimeSaved - annualRecurring;
    const threeYearValueOfTimeSaved =
      (timeSavings.totalAnnualHoursSaved * 3 - implementationHours) * boardMemberHourlyValue;

    const timeROI = {
      totalImplementationHours: implementationHours,
      annualTimeSaved: timeSavings.totalAnnualHoursSaved,
      valueOfTimeSaved: annualValueOfTimeSaved,
      netAnnualValue: netAnnualValue,
      threeYearTimeSaved: timeSavings.totalAnnualHoursSaved * 3 - implementationHours,
      threeYearValue: threeYearValueOfTimeSaved,
      efficiencyMultiplier:
        Math.round((timeSavings.totalAnnualHoursSaved / implementationHours + 1) * 10) / 10,
    };

    return {
      setup,
      monthlyRecurring,
      annualRecurring,
      timeSavings,
      efficiencyGains,
      timeROI,
    };
  };

  const calculateResults = (): CalculatorResults => {
    return {
      silver: calculatePackageResults('silver'),
      gold: calculatePackageResults('gold'),
    };
  };

  const [results, setResults] = useState<CalculatorResults>(() => calculateResults());

  useEffect(() => {
    const newResults = calculateResults();
    setResults(newResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputs.communitySize,
    inputs.boardMeetingHours,
    inputs.meetingPrepHours,
    inputs.financialReviewHours,
    inputs.residentCommunicationHours,
    inputs.documentationHours,
    inputs.violationProcessingHours,
    inputs.architecturalReviewHours,
    inputs.legalComplianceHours,
    inputs.vendorManagementHours,
    inputs.maintenanceOversightHours,
    inputs.budgetPreparationHours,
    inputs.annualMeetingPrepHours,
    inputs.insuranceRenewalHours,
    inputs.auditPreparationHours,
    inputs.emergencyResponseHours,
    inputs.selectedTier,
  ]);

  const formatHours = (hours: number): string => {
    if (hours >= 1000) {
      return `${Math.round(hours / 100) / 10}k`;
    }
    return hours.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-2">
          <Calculator className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">
            Governance Time Efficiency Calculator
          </h1>
        </div>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Calculate how much volunteer time your board can save with digital governance tools. Based
          on comprehensive HOA governance activities and realistic efficiency improvements.
        </p>
      </div>

      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Community & Governance Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Community Size */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Community Size (Parcels/Units)</Label>
            <Input
              type="number"
              value={inputs.communitySize}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInputs({ ...inputs, communitySize: parseInt(e.target.value) || 0 })
              }
              className="max-w-xs"
            />
          </div>

          {/* Meeting & Administration Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Monthly Meeting & Administration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Board Meeting Hours (per month)</Label>
                <Slider
                  value={[inputs.boardMeetingHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, boardMeetingHours: value[0] })
                  }
                  max={8}
                  min={1}
                  step={0.5}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.boardMeetingHours} hours</span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Meeting Prep & Follow-up (per month)</Label>
                <Slider
                  value={[inputs.meetingPrepHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, meetingPrepHours: value[0] })
                  }
                  max={12}
                  min={1}
                  step={0.5}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.meetingPrepHours} hours</span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Financial Review (per month)</Label>
                <Slider
                  value={[inputs.financialReviewHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, financialReviewHours: value[0] })
                  }
                  max={8}
                  min={0.5}
                  step={0.5}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.financialReviewHours} hours</span>
              </div>
            </div>
          </div>

          {/* Communication Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Communication & Documentation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Resident Communication (hours per week)</Label>
                <Slider
                  value={[inputs.residentCommunicationHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, residentCommunicationHours: value[0] })
                  }
                  max={15}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">
                  {inputs.residentCommunicationHours} hours/week
                </span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Documentation & Records (per month)</Label>
                <Slider
                  value={[inputs.documentationHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, documentationHours: value[0] })
                  }
                  max={20}
                  min={2}
                  step={1}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.documentationHours} hours</span>
              </div>
            </div>
          </div>

          {/* Enforcement Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Enforcement & Compliance (Monthly)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Violation Processing</Label>
                <Slider
                  value={[inputs.violationProcessingHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, violationProcessingHours: value[0] })
                  }
                  max={15}
                  min={0}
                  step={1}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">
                  {inputs.violationProcessingHours} hours
                </span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Architectural Review</Label>
                <Slider
                  value={[inputs.architecturalReviewHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, architecturalReviewHours: value[0] })
                  }
                  max={12}
                  min={0}
                  step={1}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">
                  {inputs.architecturalReviewHours} hours
                </span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Legal Compliance</Label>
                <Slider
                  value={[inputs.legalComplianceHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, legalComplianceHours: value[0] })
                  }
                  max={10}
                  min={0}
                  step={1}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.legalComplianceHours} hours</span>
              </div>
            </div>
          </div>

          {/* Annual Activities Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Annual High-Impact Activities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Budget Preparation (hours/year)</Label>
                <Slider
                  value={[inputs.budgetPreparationHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, budgetPreparationHours: value[0] })
                  }
                  max={80}
                  min={10}
                  step={5}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.budgetPreparationHours} hours</span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Annual Meeting Prep</Label>
                <Slider
                  value={[inputs.annualMeetingPrepHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, annualMeetingPrepHours: value[0] })
                  }
                  max={50}
                  min={5}
                  step={5}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{inputs.annualMeetingPrepHours} hours</span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Emergency Response</Label>
                <Slider
                  value={[inputs.emergencyResponseHours]}
                  onValueChange={(value: number[]) =>
                    setInputs({ ...inputs, emergencyResponseHours: value[0] })
                  }
                  max={60}
                  min={5}
                  step={5}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">
                  {inputs.emergencyResponseHours} hours/year
                </span>
              </div>
            </div>
          </div>

          {/* Package Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Package Comparison</Label>
            <div className="flex gap-2">
              {['silver', 'gold', 'both'].map((tier) => (
                <Button
                  key={tier}
                  variant={inputs.selectedTier === tier ? 'default' : 'outline'}
                  onClick={() => setInputs({ ...inputs, selectedTier: tier as any })}
                  className="capitalize"
                >
                  {tier}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      <div className="grid gap-6">
        {inputs.selectedTier === 'both' ? (
          <>
            {/* Side-by-side comparison */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Silver Results */}
              <Card className="border-2 border-slate-200">
                <CardHeader className="bg-slate-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5 text-slate-600" />
                      Silver Package
                    </CardTitle>
                    <Badge variant="secondary">
                      ${MONTANA_CONSTANTS.PRICING.SILVER}/parcel/month • $
                      {MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.SILVER}/parcel setup
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 p-6">
                  {/* Cost Summary */}
                  <div className="bg-slate-100 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        Setup Cost (1-time) ({inputs.communitySize} parcels)
                      </span>
                      <span className="font-semibold">{formatCurrency(results.silver.setup)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Cost</span>
                      <span className="font-semibold">
                        {formatCurrency(results.silver.monthlyRecurring)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-slate-300 pt-2">
                      <span className="text-gray-600">Annual Cost</span>
                      <span className="font-bold text-lg">
                        {formatCurrency(results.silver.annualRecurring)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-primary">
                        {formatHours(results.silver.timeSavings.totalAnnualHoursSaved)}
                      </div>
                      <div className="text-sm text-gray-600">Hours Saved/Year</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(results.silver.timeROI.valueOfTimeSaved)}
                      </div>
                      <div className="text-sm text-gray-600">Value of Time Saved/Year</div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Net Annual Value</div>
                      <div className="text-xl font-bold text-green-700">
                        {formatCurrency(results.silver.timeROI.netAnnualValue)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        (Time value saved - Annual cost)
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800">Time Savings Breakdown:</h4>
                    {Object.entries(results.silver.timeSavings)
                      .filter(([key]) => key !== 'totalAnnualHoursSaved')
                      .map(([key, value]) => {
                        if (typeof value === 'object' && 'savings' in value) {
                          return (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="capitalize text-gray-600">
                                {key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, (str) => str.toUpperCase())}
                              </span>
                              <span className="font-medium">{value.savings}h saved</span>
                            </div>
                          );
                        }
                        return null;
                      })}
                  </div>

                  <div className="pt-4 border-t">
                    <div className="text-center space-y-1">
                      <div className="text-sm text-gray-600">3-Year Time Saved</div>
                      <div className="text-xl font-bold text-primary">
                        {formatHours(results.silver.timeROI.threeYearTimeSaved)} hours
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Gold Results */}
              <Card className="border-2 border-yellow-200">
                <CardHeader className="bg-yellow-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-yellow-600" />
                      Gold Package
                    </CardTitle>
                    <Badge variant="secondary">
                      ${MONTANA_CONSTANTS.PRICING.GOLD}/parcel/month • $
                      {MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.GOLD}/parcel setup
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 p-6">
                  {/* Cost Summary */}
                  <div className="bg-yellow-100 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        Setup Cost (1-time) ({inputs.communitySize} parcels)
                      </span>
                      <span className="font-semibold">{formatCurrency(results.gold.setup)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Cost</span>
                      <span className="font-semibold">
                        {formatCurrency(results.gold.monthlyRecurring)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-yellow-300 pt-2">
                      <span className="text-gray-600">Annual Cost</span>
                      <span className="font-bold text-lg">
                        {formatCurrency(results.gold.annualRecurring)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-primary">
                        {formatHours(results.gold.timeSavings.totalAnnualHoursSaved)}
                      </div>
                      <div className="text-sm text-gray-600">Hours Saved/Year</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(results.gold.timeROI.valueOfTimeSaved)}
                      </div>
                      <div className="text-sm text-gray-600">Value of Time Saved/Year</div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Net Annual Value</div>
                      <div className="text-xl font-bold text-green-700">
                        {formatCurrency(results.gold.timeROI.netAnnualValue)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        (Time value saved - Annual cost)
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800">Time Savings Breakdown:</h4>
                    {Object.entries(results.gold.timeSavings)
                      .filter(([key]) => key !== 'totalAnnualHoursSaved')
                      .map(([key, value]) => {
                        if (typeof value === 'object' && 'savings' in value) {
                          return (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="capitalize text-gray-600">
                                {key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, (str) => str.toUpperCase())}
                              </span>
                              <span className="font-medium">{value.savings}h saved</span>
                            </div>
                          );
                        }
                        return null;
                      })}
                  </div>

                  <div className="pt-4 border-t">
                    <div className="text-center space-y-1">
                      <div className="text-sm text-gray-600">3-Year Time Saved</div>
                      <div className="text-xl font-bold text-primary">
                        {formatHours(results.gold.timeROI.threeYearTimeSaved)} hours
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          /* Single package view */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                {inputs.selectedTier === 'silver' ? 'Silver' : 'Gold'} Package Time Efficiency
                Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Cost Summary for Single Package View */}
              <div className="bg-linear-to-r from-slate-50 to-blue-50 rounded-lg p-6 border border-slate-200">
                <h4 className="font-semibold text-gray-800 mb-4">
                  Cost Breakdown ({inputs.communitySize} parcels)
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-600">One-Time Setup</div>
                    <div className="text-2xl font-bold text-slate-700">
                      {formatCurrency(
                        results[inputs.selectedTier as keyof CalculatorResults].setup,
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm text-gray-600">Monthly Recurring</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(
                        results[inputs.selectedTier as keyof CalculatorResults].monthlyRecurring,
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm text-gray-600">Annual Recurring</div>
                    <div className="text-2xl font-bold text-primary">
                      {formatCurrency(
                        results[inputs.selectedTier as keyof CalculatorResults].annualRecurring,
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4 text-center">
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-primary">
                    {formatHours(
                      results[inputs.selectedTier as keyof CalculatorResults].timeSavings
                        .totalAnnualHoursSaved,
                    )}
                  </div>
                  <div className="text-sm text-gray-600">Hours Saved Annually</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-green-600">
                    {formatCurrency(
                      results[inputs.selectedTier as keyof CalculatorResults].timeROI
                        .valueOfTimeSaved,
                    )}
                  </div>
                  <div className="text-sm text-gray-600">Value of Time Saved/Year</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-blue-600">
                    {
                      results[inputs.selectedTier as keyof CalculatorResults].timeROI
                        .efficiencyMultiplier
                    }
                    x
                  </div>
                  <div className="text-sm text-gray-600">Efficiency Multiplier</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-purple-600">
                    {formatHours(
                      results[inputs.selectedTier as keyof CalculatorResults].timeROI
                        .threeYearTimeSaved,
                    )}
                  </div>
                  <div className="text-sm text-gray-600">3-Year Time Saved</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Methodology Disclaimer */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-semibold text-blue-900">Time Calculation Methodology</h4>
                <p className="text-sm text-blue-800">
                  Time savings are calculated based on realistic efficiency improvements from
                  digital governance tools. Estimates are conservative and based on comprehensive
                  HOA governance activities including meeting management, communication,
                  enforcement, vendor oversight, and annual administrative tasks. Individual results
                  may vary based on community size, governance complexity, and adoption patterns.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GovernanceEfficiencyCalculator;
